import {
  MapContainer,
  TileLayer,
  AttributionControl,
  useMapEvents,
} from "react-leaflet";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MapDialog from "./map-dialog";
import MapClusterGroups from "./marker-cluster-groups";
import MapPolylines from "./map-polylines";
import PolylinesLegend from "./map-polylines-legend";
import MapBaselayerSelector from "./map-baselayer-selector";
import MapGreenCorridors from "./map-green-corridors";
import LanguageSelector from "./language-selector";
import LogoLoader from "../shared/logo-loader";
import {
  getHoboIcon,
  getTramIcon,
  getTramStopIcon,
  getTrolleyIcon,
  getTrolleyStopIcon,
  getBusIcon,
  getBusStopIcon,
  getSparkIcon,
  getSubwayStationIcon,
  getChargerIcon,
  createClusterChargerIcon,
  createClusterTrolleyIcon,
  createClusterTrolleyStopIcon,
  createClusterTramIcon,
  createClusterTramStopIcon,
  createClusterBusIcon,
  createClusterBusStopIcon,
  createClusterHoboIcon,
  createClusterSparkIcon,
  createClusterSubwayStationIcon,
  getReconstructionIcon,
  createClusterReconstructionIcon,
} from "../../services/shared";
import {
  setLoading,
  setMapDialog,
  setMapZoom,
} from "../../redux/actions/shared-actions";
import L from "leaflet";
import MapReconstructions from "./map-reconstructions";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import mapPolylinesLegend from "./map-polylines-legend";

/**
 * Used to display a leaflet map.
 * Wrapper for rest of the map components.
 * The data is provided via redux.
 * @component
 * @category Components
 * @subcategory Map
 */

const Map = () => {
  const { t } = useTranslation();

  //func to listen for map events on the leaflet map
  const MapEvents = () => {
    const dispatch = useDispatch();
    const mapEvents = useMapEvents({
      zoomend: (e) => {
        dispatch(setMapZoom(mapEvents.getZoom()));
        for (let layer in e.target._layers) {
          if (e.target._layers[layer].options.weight) {
            e.target._layers[layer].setStyle({
              weight: Math.floor(mapZoom / 2.5),
            });
          }
        }
      },
    });

    return null;
  };

  const dispatch = useDispatch();
  const {
    //kanachkova
    reconstructionsTemporary,
    reconstructionsPermanent,
    avl,
    hobo,
    spark,
    loading,
    readyStaticData,
    readyWebsocketData,
    staticData,
    navigation,
    isDialogOpen,
    mapBaselayer,
    disableWheelZoom,
    mapCenter,
    mapZoom,
    selectedLine,
  } = useSelector((state) => ({
    //kanachkova
    reconstructionsTemporary: state.staticData.reconstructionsTemporary,
    reconstructionsPermanent: state.staticData.reconstructionsPermanent,
    hobo: state.websocket.hobo,
    spark: state.websocket.spark,
    avl: state.websocket.avl,
    readyStaticData: state.shared.readyStaticData,
    readyWebsocketData: state.shared.readyWebsocketData,
    loading: state.shared.loading,
    staticData: state.staticData,
    navigation: state.navigation,
    isDialogOpen: state.shared.isMapDialogOpen,
    mapBaselayer: state.shared.mapBaselayer,
    disableWheelZoom: state.shared.disableWheelZoom,
    mapCenter: state.shared.mapCenter,
    mapZoom: state.shared.mapZoom,
    selectedLine: state.shared.selectedLine,
  }));
  const [, setIsMapDialogOpen] = useState(false);
  const handleClose = () => {
    setIsMapDialogOpen(false);
    dispatch(setMapDialog(false));
  };

  const ref = React.useRef(null);
  //depending on these utils
  //different map markers are drawn
  const dataUtils = {
    tram: {
      iconCreater: createClusterTramIcon,
      iconGetter: getTramIcon,
      data: avl.trams,
    },
    trolley: {
      iconCreater: createClusterTrolleyIcon,
      iconGetter: getTrolleyIcon,
      data: avl.trolleysAndEBuses,
    },
    bus: {
      iconCreater: createClusterBusIcon,
      iconGetter: getBusIcon,
      data: avl.buses,
    },
    subwayStations: {
      iconCreater: createClusterSubwayStationIcon,
      iconGetter: getSubwayStationIcon,
      data: staticData.subwayStations,
    },
    spark: {
      iconCreater: createClusterSparkIcon,
      iconGetter: getSparkIcon,
      data: spark,
    },
    hobo: {
      iconCreater: createClusterHoboIcon,
      iconGetter: getHoboIcon,
      data: hobo,
    },
    charger: {
      iconCreater: createClusterChargerIcon,
      iconGetter: getChargerIcon,
      data: staticData.chargers,
    },
    //kanachkova
    reconstructionsTemporary: {
      // iconCreater: createClusterReconstructionIcon,
      // iconGetter: getReconstructionIcon,
      data: reconstructionsTemporary,
    },
    reconstructionsPermanent: {
      // iconCreater: createClusterReconstructionIcon,
      // iconGetter: getReconstructionIcon,
      data: reconstructionsPermanent,
    },
  };

  useEffect(() => {
    if (readyStaticData && readyWebsocketData) {
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(true));
    }
  }, [readyStaticData, readyWebsocketData, dispatch]);

  useEffect(() => {
    if (ref.current) {
      ref.current.setUrl(mapBaselayer.url);
    }
  }, [mapBaselayer]);
  useEffect(() => {
    if (ref.current) {
      !disableWheelZoom
        ? ref.current._map.scrollWheelZoom.disable()
        : ref.current._map.scrollWheelZoom.enable();
    }
  }, [disableWheelZoom]);

  useEffect(() => {
    if (ref.current) {
      const map = ref.current._map;
      map.flyTo(mapCenter, mapZoom);
      const animatedCircleIcon = {
        icon: L.divIcon({
          className: "css-icon",
          html: '<div class="gps_ring"></div>',
          iconSize: [40, 40],
          iconAnchor: [17, 28],
        }),
      };
      const pulse = new L.Marker(mapCenter, animatedCircleIcon);
      const removePulse = () => map.removeLayer(pulse);
      if (mapZoom === 18) {
        map.once("moveend", function () {
          map.addLayer(pulse);
          setTimeout(removePulse, 3000);
        });
      }
      return () => {
        clearTimeout(removePulse);
        removePulse();
      };
    }
    // eslint-disable-next-line
  }, [mapCenter]);

  //kanachkova
  /*User notification for loading of the data
    based on the initially loaded data**/

  // useEffect(() => {
  //   let notificationText = null;

  //   const setNotificationHandler = (text, type) => {
  //     if (type == null) {
  //       notificationText = t(`${text}`)
  //       NotificationManager.success(notificationText);
  //     } else {
  //       notificationText = t(`${text}_${type}`);
  //       NotificationManager.error(notificationText);
  //     }
  //   }

  //   setTimeout(() => {
  //     if (!loading) {
  //       let data = Object.entries(staticData);

  //       data.forEach((entry) => {
  //         if (!entry[1].length) {
  //           setNotificationHandler("MISSING_DATA", `${entry[0].toLocaleUpperCase()}`);
  //         }
  //       })

  //     }
  //   }, 2000);
  //   // eslint-disable-next-line
  // }, [loading]);

  return (
    <>
      {loading ? (
        <LogoLoader />
      ) : (
        <>
          <MapDialog open={isDialogOpen} onClose={handleClose}></MapDialog>
          <MapContainer
            center={mapCenter}
            maxZoom={18}
            minZoom={12}
            scrollWheelZoom={disableWheelZoom}
            className="map-container"
            zoom={13}
            attributionControl={false}
          >
            <TileLayer
              url={mapBaselayer.url}
              attribution={mapBaselayer.attribution}
              ref={ref}
            />
            <PolylinesLegend />
            <LanguageSelector />
            <MapBaselayerSelector
              mapCenter={mapCenter}
              baselayerName={mapBaselayer.name}
            />
            <AttributionControl position="bottomright" prefix={false} />
            <MapEvents />

            {Object.keys(navigation)
              .filter(
                (type) =>
                  navigation[type].isActive &&
                  navigation[type].mapElement === "Markers"
              )
              .map((x) => {
                return (
                  <MapClusterGroups
                    key={x}
                    data={dataUtils[x].data}
                    iconCreater={dataUtils[x].iconCreater}
                    iconGetter={dataUtils[x].iconGetter}
                  />
                );
              })}

            {Object.keys(navigation)
              .filter(
                (type) =>
                  navigation[type].isActive &&
                  navigation[type].mapElement === "Polylines"
              )
              .map((x) => {
                return <MapPolylines key={x} data={staticData[x]} type={x} />;
              })}

            {/**kanachkova */}
            {Object.keys(navigation)
              .filter(
                (type) =>
                  navigation[type].isActive &&
                  navigation[type].mapElement === "Reconstructions"
              )
              .map((x) => {
                return <MapReconstructions key={x} data={staticData[x]} />;
              })}

            {Object.keys(navigation)
              .filter(
                (type) =>
                  navigation[type].isActive &&
                  navigation[type].mapElement === "Polygons"
              )
              .map((x) => {
                let corridorData = [];
                corridorData.push(
                  staticData[x],
                  avl.trams,
                  avl.trolleysAndEBuses,
                  avl.buses
                );
                return <MapGreenCorridors key={x} data={corridorData} />;
              })}

            {staticData.filteredTMData.map((line) => {
              return (
                <React.Fragment key={line.line}>
                  {navigation.tram.isActive ||
                  Object.keys(navigation)
                    .filter((x) => navigation[x].isActive)
                    .filter((x) => x.includes("greenCorridor")).length ? (
                    <>
                      {mapZoom > 14 ||
                      (selectedLine &&
                        selectedLine !== "ALL_TROLLEYS_AND_EBUSES" &&
                        selectedLine !== "ALL_TRAMS" &&
                        selectedLine !== "ALL_BUSES") ||
                      (Object.keys(navigation)
                        .filter((x) => navigation[x].isActive)
                        .filter((x) => x.includes("greenCorridor")).length &&
                        mapZoom > 14) ? (
                        <>
                          {Object.values(line)
                            .filter((x) => typeof x !== "string")
                            .map((direction) => {
                              return (
                                <MapClusterGroups
                                  key={direction.name + "-stops"}
                                  data={direction.stops}
                                  iconCreater={createClusterTramStopIcon}
                                  iconGetter={getTramStopIcon}
                                  stopData={{
                                    line: line.line,
                                    direction: direction.name,
                                  }}
                                />
                              );
                            })}
                        </>
                      ) : null}

                      {Object.values(line)
                        .filter((x) => typeof x !== "string")
                        .map((direction) => {
                          return (
                            <MapPolylines
                              key={direction.name + "-segments"}
                              data={direction.segments}
                              type={"trams"}
                            />
                          );
                        })}
                    </>
                  ) : null}
                </React.Fragment>
              );
            })}
            {staticData.filteredTBData.map((line) => {
              return (
                <React.Fragment key={line.line}>
                  {navigation.trolley.isActive ||
                  Object.keys(navigation)
                    .filter((x) => navigation[x].isActive)
                    .filter((x) => x.includes("greenCorridor")).length ? (
                    <>
                      {mapZoom > 14 ||
                      (selectedLine &&
                        selectedLine !== "ALL_TROLLEYS_AND_EBUSES" &&
                        selectedLine !== "ALL_TRAMS" &&
                        selectedLine !== "ALL_BUSES") ||
                      (Object.keys(navigation)
                        .filter((x) => navigation[x].isActive)
                        .filter((x) => x.includes("greenCorridor")).length &&
                        mapZoom > 14) ? (
                        <>
                          {Object.values(line)
                            .filter((x) => typeof x !== "string")
                            .map((direction) => {
                              return (
                                <MapClusterGroups
                                  key={direction.name + "-stops"}
                                  data={direction.stops}
                                  iconCreater={createClusterTrolleyStopIcon}
                                  iconGetter={getTrolleyStopIcon}
                                  stopData={{
                                    line: line.line,
                                    direction: direction.name,
                                  }}
                                />
                              );
                            })}
                        </>
                      ) : null}
                      {Object.values(line)
                        .filter((x) => typeof x !== "string")
                        .map((direction) => {
                          return (
                            <MapPolylines
                              key={direction.name}
                              data={direction.segments}
                              type={"trolleys"}
                            />
                          );
                        })}
                    </>
                  ) : null}
                </React.Fragment>
              );
            })}
            {staticData.filteredBData.map((line) => {
              return (
                <React.Fragment key={line.line}>
                  {navigation.bus.isActive ||
                  Object.keys(navigation)
                    .filter((x) => navigation[x].isActive)
                    .filter((x) => x.includes("greenCorridor")).length ? (
                    <>
                      {mapZoom > 14 ||
                      (selectedLine &&
                        selectedLine !== "ALL_TROLLEYS_AND_EBUSES" &&
                        selectedLine !== "ALL_TRAMS" &&
                        selectedLine !== "ALL_BUSES") ||
                      (Object.keys(navigation)
                        .filter((x) => navigation[x].isActive)
                        .filter((x) => x.includes("greenCorridor")).length &&
                        mapZoom > 14) ? (
                        <>
                          {Object.values(line)
                            .filter((x) => typeof x !== "string")
                            .map((direction) => {
                              return (
                                <MapClusterGroups
                                  key={direction.name + "-stops"}
                                  data={direction.stops}
                                  iconCreater={createClusterBusStopIcon}
                                  iconGetter={getBusStopIcon}
                                  stopData={{
                                    line: line.line,
                                    direction: direction.name,
                                  }}
                                />
                              );
                            })}
                        </>
                      ) : null}
                      {Object.values(line)
                        .filter((x) => typeof x !== "string")
                        .map((direction) => {
                          return (
                            <MapPolylines
                              key={direction.name}
                              data={direction.segments}
                              type={"buses"}
                            />
                          );
                        })}
                    </>
                  ) : null}
                </React.Fragment>
              );
            })}
          </MapContainer>
        </>
      )}
    </>
  );
};

export default React.memo(Map);
